import { Bookmark, Bot, Users } from 'lucide-react';
import type { ChatSummary } from '../api/client.ts';
import { Avatar } from './Avatar.tsx';

/**
 * The tile beside a chat's name, wherever a list of chats is drawn.
 *
 * Which picture and which glyph a row wears is four facts about the chat and
 * none about the list, and it was written out twice — in the chat list and in
 * the search results — before there was a third kind of chat to get wrong in
 * both places. It is one function now, and each caller passes only what is its
 * own business: the size and shape, through `className`, and how big the glyph
 * inside it should be.
 *
 * The glyphs are what a chat with no picture on it says about itself:
 *
 * - a group wears the people icon, so it reads as a group at a glance rather
 *   than only once you notice the snippet;
 * - «Заметки» wear a bookmark, for the same reason and because the alternative
 *   is the owner's own initial on the one row that is not about a person;
 * - a bot wears its own glyph because it has no photograph and never will, and
 *   an initial would leave it looking like somebody whose picture has not
 *   loaded yet.
 *
 * Everything else falls through to the first letter of the name, which is what
 * a person without a photograph gets.
 */
export function ChatAvatar({
  chat,
  className,
  glyph,
}: {
  /** `undefined` where the list holds a result for a chat it has not loaded. */
  chat: ChatSummary | undefined;
  /** The size and shape, e.g. `chat-row-avatar`. */
  className?: string;
  /** The size of the glyph drawn when there is no picture. */
  glyph: number;
}) {
  const own = chat?.kind === 'group' || chat?.kind === 'notes';
  return (
    <Avatar
      className={className}
      photoId={own ? chat.avatarPhotoId : chat?.peer?.avatarPhotoId}
      // A group and a notes chat are named after themselves, so their name is
      // not an initial to fall back to — the glyph below is what they wear.
      name={own ? undefined : chat?.title}
    >
      {chat?.kind === 'group' ? (
        <Users size={glyph} />
      ) : chat?.kind === 'notes' ? (
        <Bookmark size={glyph} />
      ) : chat?.bot ? (
        <Bot size={glyph} />
      ) : undefined}
    </Avatar>
  );
}
